// The checklist, bottom left (Features 4: "connecting, in the order that matters"). It leads with one step,
// Connect your agent, and only once that is done shows the rest, each with a line on what it gets you. A step
// ticks itself when it is really connected, the user can tick one off, done steps fold away, and the checklist
// folds away once every step is done. Its ticks and its X are the account's (shared/onboarding.ts).

import { useEffect, useState } from "react";
import { Check, ChevronDown, ListChecks, Sparkles, X } from "lucide-react";
import { STEP_IDS, tickedOff, type StepId } from "../../shared/onboarding";
import { cx } from "../lib/util";
import { hideChecklist, isOn, oldHidden, tickStep, useOnboarding, writeOnboarding } from "../state/onboarding";
import { set, useStore } from "../state/store";
import { agentConnected, ConnectAgent, useAgentLink } from "./ConnectAgent";
import "./onboarding.css";

const DOCS = "https://contextbolt.com/docs/board";

interface Step {
  id: StepId;
  label: string;
  /** what it gets you, in a line */
  gets: string;
  done: boolean;
  go: () => void;
  help: string;
}

function useSteps(): Step[] {
  const link = useAgentLink();
  const connections = useStore((s) => Object.keys(s.connections).length > 0);
  const ownMcps = useStore((s) => !!(s.runner?.ownMcps?.claude?.on || s.runner?.ownMcps?.codex?.on));
  const apis = useStore((s) => Object.keys(s.services).length > 0 || Object.values(s.pulls).some((p) => p.kind === "http" || p.kind === "rss" || p.kind === "ical"));
  const folders = useStore((s) => (s.runner?.folders?.length ?? 0) > 0);
  const computers = useStore((s) => (s.runner?.computers?.length ?? 0) > 0);
  return [
    { id: "agent", label: "Connect your agent", gets: "It builds your board with you, fills it in and answers in the chat.", done: agentConnected(link), go: () => set({ panel: "connect", panelArg: { tab: "ai" } }), help: `${DOCS}/your-agent/` },
    { id: "mcps", label: "Bring your MCPs", gets: "Stripe, Linear, Notion and more, read by your agent and shown in widgets.", done: connections || ownMcps, go: () => set({ panel: "connect", panelArg: { found: true } }), help: `${DOCS}/connections/#bring-your-mcp-servers` },
    { id: "apis", label: "Connect your APIs", gets: "Your Stripe revenue on the board, kept current.", done: apis, go: () => set({ panel: "connect", panelArg: { paste: true } }), help: `${DOCS}/connections/#any-api-feed-calendar-or-mcp-server` },
    { id: "files", label: "Connect your project files", gets: "Your agent reads the folders you choose, and changes only those you allow.", done: folders, go: () => set({ panel: "connect", panelArg: { folders: true } }), help: `${DOCS}/your-agent/#project-folders` },
    { id: "computers", label: "Connect your computers", gets: "Every Claude Code and Codex session on them, and what waits for you, in one place.", done: computers, go: () => set({ panel: "connect", panelArg: { computers: true } }), help: `${DOCS}/your-agent/#link-your-computer` },
  ];
}

export function Checklist() {
  const hidden = useOnboarding((o) => isOn(o.hidden)) || oldHidden();
  const tickedKey = useOnboarding((o) => STEP_IDS.filter((id) => tickedOff(o, id)).join(","));
  const ticked = new Set(tickedKey.split(",").filter(Boolean));
  const link = useAgentLink();
  const steps = useSteps();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (open) writeOnboarding({ seen: Date.now() }, "Checklist seen");
  }, [open]);
  // asked for from elsewhere (a Button pressed with no agent): it opens on its first step
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("cb:connect-agent", show);
    return () => window.removeEventListener("cb:connect-agent", show);
  }, []);
  const settled = (s: Step) => s.done || ticked.has(s.id);
  const agent = steps[0];
  const agentDone = settled(agent);
  const rest = steps.slice(1);
  const doneCount = steps.filter(settled).length;
  if (hidden || doneCount === steps.length) return null;
  // the one-click line stays in sight once a computer has the agent, until it is used or ticked off
  const readyLine = link.s === "ready" && !ticked.has("agent");
  const next = rest.find((s) => !settled(s));
  if (!open)
    return (
      <button className={cx("corner-pill island ob-pill", !agentDone && "is-lead")} data-ob="checklist" onClick={() => setOpen(true)}>
        {agentDone ? <ListChecks size={14} className="corner-pill-ico" /> : <Sparkles size={14} className="corner-pill-ico" />}
        <span>{agentDone ? (next ? `Next: ${next.label}` : "Connect your tools") : "Connect your agent"}</span>
        {agentDone && (
          <span className="faint">
            {doneCount} of {steps.length}
          </span>
        )}
      </button>
    );
  return (
    <div className="corner-card island connect-steps ob-check" data-ob="checklist-open">
      <div className="corner-head">
        <h3>{agentDone && !readyLine ? "Next steps" : "Connect your agent"}</h3>
        <span className="inline-actions">
          <button className="icon-btn sm" aria-label="Fold away" data-tip="Fold away" onClick={() => setOpen(false)}>
            <ChevronDown size={14} />
          </button>
          <button className="icon-btn sm" aria-label="Hide for good" data-tip="Hide for good" onClick={hideChecklist}>
            <X size={13} />
          </button>
        </span>
      </div>
      {!agentDone || readyLine ? (
        <div className="ob-lead-step">
          <p>{agent.gets}</p>
          <ConnectAgent link={link} onUse={() => tickStep("agent", true)} />
          {!agentDone && (
            <div className="ob-step-foot">
              <a href={agent.help} target="_blank" rel="noreferrer">
                See how
              </a>
              <button className="link-btn" onClick={() => tickStep("agent", true)}>
                Skip this step
              </button>
            </div>
          )}
        </div>
      ) : null}
      {agentDone && (
        <>
          {readyLine && <div className="ob-sec">Then, when you want them</div>}
          <div className="steps">
            {rest
              .filter((s) => !settled(s))
              .map((s) => (
                <div key={s.id} className="step" data-step={s.id}>
                  <button className="step-check" aria-label={`Tick ${s.label} off`} aria-pressed={false} data-tip="Tick it off" onClick={() => tickStep(s.id, true)} />
                  <button className="step-main" onClick={s.go}>
                    <span className="step-label">{s.label}</span>
                    <span className="step-sub">{s.gets}</span>
                  </button>
                  <a className="step-help" href={s.help} target="_blank" rel="noreferrer" data-tip="See how to do this">
                    See how
                  </a>
                </div>
              ))}
          </div>
          {doneCount > 1 && (
            <p className="ob-done-line">
              <Check size={12} strokeWidth={2.6} /> {doneCount} of {steps.length} done
              {rest.some((s) => !s.done && ticked.has(s.id)) && (
                <button className="link-btn" onClick={() => writeOnboarding({ ticked: Object.fromEntries(rest.filter((s) => ticked.has(s.id)).map((s) => [s.id, -Date.now()])) }, "Untick steps")}>
                  Show ticked steps again
                </button>
              )}
            </p>
          )}
          <button className="link-btn corner-all" onClick={() => writeOnboarding({ ticked: Object.fromEntries(steps.map((s) => [s.id, Date.now()])) }, "Mark all done")}>
            Mark all done
          </button>
        </>
      )}
    </div>
  );
}
